import { useState } from 'react';
import { Link } from 'react-router-dom';
// MUI
import {
  AppBar,
  Container,
  IconButton,
  Toolbar,
  Typography,
  Box,
  useMediaQuery,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Notifications, AccountCircle, Logout, SettingsOutlined } from '@mui/icons-material/';
import * as icons from '@mui/icons-material'; // För dynamiska moduler
// COMPONENTS
import EasyStaffLogo from 'src/assets/images/easystaff_logo-header.png';
import { NavigationMenu } from 'src/shared/components/navigationMenu';
import { Colors } from 'src/shared/components/styles/colors';
import { SwitchLanguageButton } from 'src/shared/components/switchLanguageButton';
// SERVICES
import { RoutesEnum } from 'src/shared/enums/routes.enum';
import userManager from 'src/services/identity/userManager';
import { useGlobals } from 'src/services/contexts/appContext';
import { useTranslation } from 'react-i18next';
import { NotificationList } from 'src/modules/notifications/components/notificationList';
import ChatNotificationBadge from 'src/modules/chat/components/chatNotificationBadge';
import { UserTypeEnum } from 'src/shared/enums/userType.enum';
import { AvatarWithBorderMe } from 'src/modules/attendance/components/avatarWithBorderMe';
import { RootStateType } from 'src/services/redux/reducers';
import { useSelector } from 'react-redux';
import { ModuleDisplayName, getRouteForModule } from './helper/modulesHelper';
// import { useInterval } from 'src/services/helpers/useInterval';
// import { Routes, usePrefetchRoutes } from 'src/services/helpers/prefetch';
// import { useFetchInfinityChatrooms, useFetchPinnedChatrooms } from 'src/modules/chat/hooks/queries/useFetchChatList';

const TopNavigation = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMediumOrLess = useMediaQuery(theme.breakpoints.down('lg'));
  // Globals
  const { loggedInUser, setShowChatModal, showChatModal, checkInStatusUser } = useGlobals();
  const { modules } = useSelector((state: RootStateType) => state.appState);
  const { unreadNotifications } = useGlobals();
  // Locals
  const [anchorProfileElement, setAnchorProfileElement] = useState<null | HTMLElement>(null);
  const [anchorNoticeElement, setAnchorNoticeElement] = useState<null | HTMLElement>(null);
  const openProfile = Boolean(anchorProfileElement);
  const openNotification = Boolean(anchorNoticeElement);

  // const pinnedChatrooms = useFetchPinnedChatrooms({
  //   SortCol: 'LastActivityAt',
  //   SortDir: 'desc',
  //   Filter: '',
  // });
  // const chatlist = useFetchInfinityChatrooms({
  //   SortCol: 'LastActivityAt',
  //   SortDir: 'desc',
  //   Filter: '',
  // });

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorProfileElement(event.currentTarget);
  };

  const handleNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorNoticeElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorProfileElement(null);
    setAnchorNoticeElement(null);
  };

  return (
    <Container sx={{ m: 0, p: 0 }}>
      <AppBar position='fixed' elevation={0} sx={{ backgroundColor: Colors.GRAY_FAIR }}>
        <Toolbar
          sx={{
            display: 'flex',
            backgroundColor: 'white',
            color: 'text.primary',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 0.3 }}>
            <Typography
              color='inherit'
              component={Link}
              to={RoutesEnum.Start}
              sx={{
                display: 'flex',
                '& a': { textDecoration: 'none', color: 'primary' },
              }}
            >
              <img
                src={EasyStaffLogo}
                srcSet={EasyStaffLogo}
                style={{
                  height: 40,
                  objectFit: 'contain',
                }}
                alt='logo-company'
                loading='lazy'
              />
            </Typography>
          </Box>
          {/* * * Dynamiska menyval * * */}
          <Box
            style={{
              display: isTablet ? 'none' : 'flex',
              flex: 1,
              justifyContent: 'center',
            }}
          >
            <NavigationMenu listItemDirection='column' listDirection='row' justifyContent='center' padding={0} />
          </Box>

          {/* * * Knappar i TopNavigation * * */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flex: isTablet ? 0.6 : 0.3,
              gap: isTablet ? 2 : 1,
            }}
          >
            <Box
              style={{
                display: isMediumOrLess ? 'none' : 'block',
              }}
            >
              <SwitchLanguageButton />
            </Box>

            {/* * * Chat * * */}
            <IconButton color='primary' aria-label='chat' sx={{ color: Colors.BLACK }} onClick={() => setShowChatModal(!showChatModal)}>
              <ChatNotificationBadge />
            </IconButton>

            <IconButton
              id='notification-menu'
              aria-label={t('common:notifications')}
              aria-controls={openNotification ? 'notification-menu' : undefined}
              aria-haspopup='true'
              aria-expanded={openNotification ? 'true' : undefined}
              onClick={handleNotificationsOpen}
              sx={{
                opacity: 1,
                color: Colors.BLACK,
              }}
            >
              <Badge badgeContent={unreadNotifications?.length} color='warning' max={99} overlap='circular'>
                <Notifications aria-label={t('common:notifications') as string} sx={{ fontSize: 26 }} />
              </Badge>
            </IconButton>

            {/* * * Avatar med status * * */}
            <IconButton
              id='profile-menu'
              aria-controls={openProfile ? 'profile-menu' : undefined}
              aria-haspopup='true'
              aria-expanded={openProfile ? 'true' : undefined}
              onClick={handleProfileMenuOpen}
            >
              {checkInStatusUser && checkInStatusUser?.userImage ? (
                <AvatarWithBorderMe size={36} userToShow={checkInStatusUser} showThemeHat smallHat />
              ) : (
                <AccountCircle
                  sx={{
                    color: Colors.ACCENT_DARK,
                    fontSize: 36,
                  }}
                />
              )}
            </IconButton>
            <Menu id='profile-menu' anchorEl={anchorProfileElement} open={openProfile} onClose={handleClose}>
              {/* * * Meny på mindre skärmar visas bara genom profilmenyn * * */}
              {isTablet ? (
                <Box>
                  {modules
                    ?.filter((item) => item.Title !== 'Chat') // Chatt har en separat plats i menyn i webbappen
                    .map((item) => {
                      const capitalizeFirstLetter = (str?: string): string => {
                        if (!str) return '';
                        return str.charAt(0).toUpperCase() + str.slice(1);
                      };
                      const iconKey = capitalizeFirstLetter(item.WebIcon) as keyof typeof icons;
                      const IconComponent = icons[iconKey] || icons.ErrorOutline;
                      return (
                        <MenuItem key={item.Title} component={Link} to={getRouteForModule(item.Title)} onClick={handleClose}>
                          <ListItemIcon aria-label={item.Title}>
                            <IconComponent sx={{ color: Colors.ACCENT_DARK }} />
                          </ListItemIcon>
                          <ListItemText>
                            <ModuleDisplayName moduleTitle={item.Title} />
                          </ListItemText>
                        </MenuItem>
                      );
                    })}
                  <Divider />
                </Box>
              ) : (
                ''
              )}
              {/* * * Hårdkodade items i profilmenyn * * */}
              <MenuItem component={Link} to={RoutesEnum.Profile} onClick={handleClose}>
                <ListItemIcon aria-label={t('common:profile')}>
                  <AccountCircle sx={{ color: Colors.ACCENT_DARK }} />
                </ListItemIcon>
                <ListItemText>{t('profile')}</ListItemText>
              </MenuItem>
              {loggedInUser?.UserType === UserTypeEnum.Admin && (
                <MenuItem component={Link} to={RoutesEnum.Admin} onClick={handleClose}>
                  <ListItemIcon aria-label={t('Admin')}>
                    <SettingsOutlined sx={{ color: Colors.ACCENT_DARK }} />
                  </ListItemIcon>
                  <ListItemText>{t('Admin')}</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={() => userManager.signoutRedirect()}>
                <ListItemIcon aria-label={t('common:logout')}>
                  <Logout sx={{ color: Colors.ACCENT_DARK }} />
                </ListItemIcon>
                <ListItemText>{t('common:logout')}</ListItemText>
              </MenuItem>
              {/* * * Mindre skärmar visar språkväljaren i profilmenyn * * */}
              {isMediumOrLess && (
                <MenuItem
                  sx={{
                    justifyContent: 'center',
                  }}
                >
                  <ListItemIcon>
                    <SwitchLanguageButton />
                  </ListItemIcon>
                </MenuItem>
              )}
            </Menu>

            <Menu
              id='notification-menu'
              anchorEl={anchorNoticeElement}
              open={openNotification}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  width: { xs: '80%', md: '50%', lg: '30%' },
                  height: '100vh',
                },
              }}
            >
              <NotificationList />
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
    </Container>
  );
};
export default TopNavigation;
